import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Tooltip,
  IconButton
} from "@mui/material"
import { InfoOutlined } from "@mui/icons-material"
import { styled } from "@mui/system"
import CommonModal from "components/CommonModal"
import AppPagination from "components/StyledComponents/AppPagination"

const SummaryCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.drawer.main,
  padding: theme.spacing(2),
  borderRadius: "8px",
  textAlign: "left",
  border: `1px solid ${theme.palette.background.default}`,
  minHeight: "80px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between"
}))

const StyledTable = styled(Table)(({ theme }) => ({
  "& .MuiTableCell-root": {
    padding: "12px 16px",
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.background.default}`
  },
  "& .MuiTableCell-head": {
    top: -1,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    fontSize: "12px",
    fontWeight: 400
  }
}))

const UserDetailsModal = ({ open, onClose, metricLabel, data }) => {
  const theme = useTheme()

  if (!data) return null

  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCurrentPage(1)
  }, [data])

  if (!data) return null

  const itemsPerPage = 10
  const totalPages = Math.ceil((data.users?.length || 0) / itemsPerPage)
  const paginatedUsers = (data.users || []).slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const summaryItems = [
    { label: "Count", value: data.count ?? "-" },
    { label: "Numerator", value: data.numerator ?? "-" },
    { label: "Denominator", value: data.denominator ?? "-" },
    { label: "Percentage", value: data.percentage ?? "-", isHighlight: true }
  ].filter((item) => item.value !== "-")

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      hideAction
      maxWidth="md"
      sx={{ p: 4, backgroundColor: theme.palette.background.default }}>
      <Typography variant="h3" align="center" mb={2} color={theme.palette.text.primary}>
        {metricLabel} Users
      </Typography>

      {summaryItems.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {summaryItems.map((item, index) => (
            <Grid item xs={3} key={index}>
              <SummaryCard>
                <Typography variant="caption" color={theme.palette.text.secondary}>
                  {item.label}
                </Typography>
                <Typography
                  variant="h2Italic"
                  color={theme.palette.secondary.default}
                  fontWeight={500}>
                  {item.value}
                </Typography>
              </SummaryCard>
            </Grid>
          ))}
          {data.total_opens != null && metricLabel !== "Preshift Only" && (
            <Grid item xs={3}>
              <SummaryCard>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" color={theme.palette.text.secondary}>
                    Preshift Opens
                  </Typography>
                  <Tooltip
                    placement="top"
                    title="Total number of times the pre-shift card was opened across all users (one user opening it 3 times counts as 3)"
                    arrow>
                    <IconButton size="small" sx={{ p: 0, color: "text.secondary" }}>
                      <InfoOutlined sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography
                  variant="h2Italic"
                  color={theme.palette.secondary.default}
                  fontWeight={500}>
                  {data?.total_opens}
                </Typography>
              </SummaryCard>
            </Grid>
          )}
        </Grid>
      )}

      <TableContainer component={Box} sx={{ maxHeight: 400, overflow: "auto" }}>
        <StyledTable stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="10%">#</TableCell>
              <TableCell width="30%">Name</TableCell>
              <TableCell width="30%">Email</TableCell>
              {metricLabel !== "Preshift Only" &&
                data.users?.some((user) => user?.view_count != null) && (
                  <TableCell align="center" width="20%">
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                      View Count
                      <Tooltip
                        placement="top"
                        title="How many times this specific user opened the pre-shift card during the period"
                        arrow>
                        <IconButton size="small" sx={{ p: 0, color: "text.secondary" }}>
                          <InfoOutlined sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user, index) => (
              <TableRow key={user.id || index}>
                <TableCell>{(currentPage - 1) * itemsPerPage + index + 1}</TableCell>
                <TableCell>{user.full_name || "-"}</TableCell>
                <TableCell>{user.email || "-"}</TableCell>
                {metricLabel !== "Preshift Only" && user?.view_count != null && (
                  <TableCell align="center">{user.view_count || "-"}</TableCell>
                )}
              </TableRow>
            ))}
            {(!data.users || data.users.length === 0) && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" sx={{ py: 2 }}>
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
      {data.users?.length > itemsPerPage && (
        <Box sx={{ mt: 2 }}>
          <AppPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </Box>
      )}
    </CommonModal>
  )
}

export default UserDetailsModal
